import { constants } from "@qgiv/core-js";
import {
    getCategoriesAttachedToClassification,
    getNewCategoryValueToSet,
} from "./categoryHelpers";

const {
    ENUMS: { EntityType },
} = constants;

/**
 * @public
 * @function filterClassificationsWhenLimitedTeamJoiningToClass
 * @param {object} settings Settings object
 * @param {Array} [settings.filteredClassifications] Array of Available Classification
 * @param {Array} [settings.teams] Array of Teams Available to join
 * @param {boolean} [settings.Team] Team selected for Primary registrant
 * @returns {Array} Array of classifications that are available when this Team is selected
 *  and teams are limited to joining a specific classification
 */
export const filterClassificationsWhenLimitedTeamJoiningToClass = ({
    filteredClassifications,
    teams,
    Team,
}) => {
    const currentTeam = Team
        ? teams.find((team) => String(team.id) === Team)
        : {};
    const associatedClassificationId = currentTeam?.classification || 0;

    const allowableClassification =
        associatedClassificationId && filteredClassifications.length > 0
            ? filteredClassifications.find(
                  (classification) =>
                      classification.id === associatedClassificationId,
              )
            : undefined;

    return allowableClassification ? [allowableClassification] : [];
};

/**
 * @public
 * @function filterClassificationsWhenTeamIsPreSelected
 * @param {object} settings Settings object
 * @param {Array} [settings.filteredClassifications] Array of Available Classification
 * @param {Array} [settings.teams] Array of Teams Available to join
 * @param {boolean} [settings.Team] Team selected for Primary registrant
 * @param {Array} [settings.categories] Available Categories
 * @returns {Array} Array of classifications that are available when this Team is selected.
 * Determined by checking which classifications are attached to categories that allow Teams
 */
export const filterClassificationsWhenTeamIsPreSelected = ({
    filteredClassifications,
    teams,
    Team,
    categories,
}) => {
    const currentTeam = Team
        ? teams.find((team) => String(team.id) === Team)
        : {};

    const newFilteredClassifications =
        filteredClassifications?.length > 0 &&
        Object.keys(currentTeam).length > 0
            ? filteredClassifications?.filter((classification) => {
                  const categoriesAttachedToClassification =
                      getCategoriesAttachedToClassification({
                          categories,
                          classificationId: classification.id,
                      });

                  const hasCategoriesThatAllowTeams =
                      categoriesAttachedToClassification?.some(
                          (cat) =>
                              cat.attachTeamMembers || cat.attachTeamCaptains,
                      );

                  return hasCategoriesThatAllowTeams;
              })
            : [];

    return newFilteredClassifications;
};

/**
 * @public
 * @function filterClassificationsThatAreSoldOut
 * @param {Array} filteredClassifications Array of Available Classification
 * @returns {Array} Array of classifications that are available and not sold out.
 * This is especially useful when we hit a race condition during processing
 */
export const filterClassificationsThatAreSoldOut = (
    filteredClassifications,
) => {
    // Need to filter out classifications with registrant limits that are sold out
    const filterClassifications = filteredClassifications.filter(
        ({ registrantSpotsLeft = -1 }) => {
            // `-1` signals that a classification has unlimited spots.
            // All classifications should have unlimited spots unless a limit
            // exist. A classification is sold out if `registrantSpotsLeft`
            // reaches `0`.
            const soldOut = registrantSpotsLeft === 0;
            return !soldOut;
        },
    );

    return filterClassifications;
};

/**
 * @public
 * @function filterOutClassificationsWithPrimaryOnlyCategoriesWhenMultipleRegistrantsAdded
 * @param {Array} filteredClassifications Array of Available Classification
 * @param {Array} categories Available Categories
 * @returns {Array} Array of classifications that are available when there are already more than
 * one registrants in summary. This filters out any classifications that are tied to Categories which
 * all only allow primary registrants.
 */
export const filterOutClassificationsWithPrimaryOnlyCategoriesWhenMultipleRegistrantsAdded =
    (filteredClassifications, categories) => {
        const newFilteredClassifications =
            filteredClassifications.length > 0
                ? filteredClassifications.filter((classification) => {
                      const categoriesAttachedToClassification =
                          getCategoriesAttachedToClassification({
                              categories,
                              classificationId: classification.id,
                          });

                      const hasCategoriesThatAllowSubregistrants =
                          categoriesAttachedToClassification?.some(
                              (cat) => cat.attachSubregistrants,
                          );

                      return hasCategoriesThatAllowSubregistrants;
                  })
                : [];
        return newFilteredClassifications;
    };

/**
 * @public
 * @function filterOutClassificationsWithTeamCreateOnlyCategoriesWhenLimitForTeamsHasBeenReached
 * @param {Array} filteredClassifications Array of Available Classification
 * @param {Array} categories Available Categories
 * @returns {Array} Array of classifications that are available when a classification's team  limit has
 * been reached and the only Categories created are Team Create.
 */
export const filterOutClassificationsWithTeamCreateOnlyCategoriesWhenLimitForTeamsHasBeenReached =
    (filteredClassifications, categories) => {
        const newFilteredClassifications =
            filteredClassifications.length > 0
                ? filteredClassifications.filter((classification) => {
                      const classificationTeamCaptainHasReachedMax =
                          classification?.teamLimit > 0 &&
                          classification?.teamSpotsLeft === 0;

                      // Return early if this limit has not been reached
                      if (!classificationTeamCaptainHasReachedMax)
                          return classification;

                      const categoriesAttachedToClassification =
                          getCategoriesAttachedToClassification({
                              categories,
                              classificationId: classification.id,
                          });

                      const hasCategoriesThatAllowTeamJoinOrIndividual =
                          categoriesAttachedToClassification?.some(
                              (cat) =>
                                  cat.attachTeamMembers || cat.attachIndividual,
                          );

                      return hasCategoriesThatAllowTeamJoinOrIndividual;
                  })
                : [];
        return newFilteredClassifications;
    };

/**
 * @public
 * @function filterClassificationsBasedOnPresetsOrUserSelections
 * @param {object} settings Settings object
 * @param {Array} [settings.categories] Available Categories
 * @param {Array} [settings.classifications] Array of Available Classification
 * @param {Array} [settings.teams] Array of Teams Available to join
 * @param {Array} [settings.registrations] Array of all registrations currently saved
 * in Redux
 * @param {boolean} [settings.limitTeamJoiningToClassification] classification setting for limiting
 * the joining of a team to a specific classification
 * @returns {Array} Array of classifications that are available when all settings and user
 * selections are taken into account
 */
export const filterClassificationsBasedOnPresetsOrUserSelections = ({
    categories,
    classifications,
    teams,
    registrations,
    limitTeamJoiningToClassification,
}) => {
    const { Team } = registrations[0];

    let filteredClassifications = classifications;

    // Return early if there are no classifications
    if (filteredClassifications?.length === 0) return classifications;

    if (limitTeamJoiningToClassification && Team) {
        filteredClassifications =
            filterClassificationsWhenLimitedTeamJoiningToClass({
                filteredClassifications,
                teams,
                Team,
            });
    }

    if (!limitTeamJoiningToClassification && Team) {
        filteredClassifications = filterClassificationsWhenTeamIsPreSelected({
            filteredClassifications,
            teams,
            Team,
            categories,
        });
    }

    if (registrations.length > 1) {
        filteredClassifications =
            filterOutClassificationsWithPrimaryOnlyCategoriesWhenMultipleRegistrantsAdded(
                filteredClassifications,
                categories,
            );
    }

    filteredClassifications = filterClassificationsThatAreSoldOut(
        filteredClassifications,
    );

    filteredClassifications =
        filterOutClassificationsWithTeamCreateOnlyCategoriesWhenLimitForTeamsHasBeenReached(
            filteredClassifications,
            categories,
        );

    return filteredClassifications;
};

/**
 * @public
 * @function fieldsToSetWithClassificationSelection
 * @param {object} settings Settings object
 * @param {Array} [settings.categories] Available Categories
 * @param {Array} [settings.classifications] Array of Available Classification
 * @param {boolean} [settings.limitTeamJoiningToClassification] classification setting for limiting
 * the joining of a team to a specific classification
 * @param {object} [settings.registration] current registration saved in Redux
 * in Redux
 * @param {Array} [settings.teams] Array of Teams Available to join
 * @param {object} [settings.values] Formik values for current page
 * @returns {object} Object with all the Formik fields that need to be reset and flags to
 * determine when we should set them.
 */
export const fieldsToSetWithClassificationSelection = ({
    categories,
    classifications,
    limitTeamJoiningToClassification,
    registration,
    teams,
    values,
}) => {
    const { Team, Registration_Role, Category } = registration;

    const selectedTeam = Team
        ? teams.find(
              (team) =>
                  Number(Team) === team.id ||
                  (Team === "new-team" && Team === team.id),
          )
        : {};

    const selectedClassification = values.Classification
        ? classifications.find(
              (classification) =>
                  Number(values.Classification) === Number(classification.id),
          )
        : {};

    const selectedCategory = Category
        ? categories.find(
              (category) => Number(Category) === Number(category.id),
          )
        : {};
    // Check selected Classification settings
    // is team limited to classification
    let shouldResetData = false;
    let newRegistrationValuesToSet = {};
    let shouldResetCategory = false;
    let newCategoryValueToSet = [];

    const clearTeamData = {
        Team: "",
        Team_Captain: false,
        Registration_Role: [],
        Needs_To_Be_Reviewed: true,
    };

    // Team Check
    if (
        (Registration_Role?.length > 0 &&
            Registration_Role[0] === `${EntityType.TEAM}`) ||
        Team
    ) {
        if (
            limitTeamJoiningToClassification &&
            Object.keys(selectedTeam).length > 0
        ) {
            const selectClassificationSupportsSetTeam =
                Number(selectedTeam.classification) ===
                Number(selectedClassification.id);

            if (!selectClassificationSupportsSetTeam) {
                newRegistrationValuesToSet = clearTeamData;
                shouldResetData = true;
            }
        }
    }

    if (
        Object.keys(selectedCategory)?.length > 0 &&
        selectedCategory?.attachedClassifications?.length > 0
    ) {
        const isClassificationAttachedToCurrentCategory =
            selectedCategory.attachedClassifications.includes(
                Number(selectedClassification.id),
            );

        shouldResetCategory = !isClassificationAttachedToCurrentCategory;

        newCategoryValueToSet = getNewCategoryValueToSet(
            categories,
            selectedClassification,
        );
    }

    const fieldsToSetValues = {
        newCategoryValueToSet,
        newRegistrationValuesToSet,
        shouldResetCategory,
        shouldResetData,
    };
    return fieldsToSetValues;
};
